// Mailboxes tab: the mailboxes the Invoice Collector reads (it_mailboxes), with
// Last Read and Access from the latest run's automation_log rows.

// REACT IMPORTS
import React, { useCallback, useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Alert, Button, Checkbox, CircularProgress, Collapse, FormControlLabel, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import InfoIcon from "@mui/icons-material/Info";

// API, CONSTANTS
import { getAPICall, patchAPICall, postAPICall } from "../../../config/apiCalls";
import { GRAY, GREEN, RED, apiError, fmtDateTime, pillStyle } from "../itBudgetUtils";

const BLANK = { email_address: "", display_name: "", lookback_days: 2, processed_folder: "Receipts", active: true };

const MailboxesTab = ({ canEdit }) => {
	const { instance, accounts } = useMsal();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(null); // null, "new", or a mailbox id
	const [form, setForm] = useState(BLANK);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setRows((await getAPICall(instance, accounts[0], "/api/it-budget/mailboxes")).data);
		} catch (err) {
			setError(apiError(err, "Couldn't load mailboxes."));
		} finally {
			setLoading(false);
		}
	}, [instance, accounts]);

	useEffect(() => {
		load();
	}, [load]);

	const startEdit = (row) => {
		setError("");
		setEditing(row ? row.id : "new");
		setForm(row ? { ...row, active: Boolean(row.active) } : BLANK);
	};

	const save = async () => {
		setSaving(true);
		setError("");
		try {
			const res =
				editing === "new"
					? await postAPICall(instance, accounts[0], "/api/it-budget/mailboxes", form)
					: await patchAPICall(instance, accounts[0], `/api/it-budget/mailboxes/${editing}`, form);
			setRows(res.data.mailboxes);
			setEditing(null);
		} catch (err) {
			setError(apiError(err, "Couldn't save the mailbox."));
		} finally {
			setSaving(false);
		}
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div className="itBudgetBanner">
				<InfoIcon fontSize="small" style={{ color: "#184c93" }} />
				<span>
					The Invoice Collector searches every active mailbox below for every vendor's sender. It can open a mailbox only if it is also a member of the{" "}
					<b>IT Budget Invoice Mailboxes</b> group in Microsoft 365; ask the M365 admin to add new mailboxes there.
				</span>
			</div>
			{canEdit && (
				<div style={{ display: "flex", justifyContent: "flex-end" }}>
					<Button variant="contained" startIcon={<AddIcon />} onClick={() => startEdit(null)}>
						Add Mailbox
					</Button>
				</div>
			)}
			<div className="itBudgetSplit">
				<div className="itBudgetSplitMain">
					{loading ? (
						<div className="itBudgetEmpty">
							<CircularProgress />
						</div>
					) : (
						<div className="itBudgetTableContainer">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Address</TableCell>
										<TableCell>Display Name</TableCell>
										<TableCell>Lookback</TableCell>
										<TableCell>Processed Folder</TableCell>
										<TableCell>Last Read</TableCell>
										<TableCell>Access</TableCell>
										<TableCell>Active</TableCell>
										{canEdit && <TableCell />}
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((m) => (
										<TableRow key={m.id}>
											<TableCell className="itBudgetMono">
												<b>{m.email_address}</b>
											</TableCell>
											<TableCell>{m.display_name || "—"}</TableCell>
											<TableCell>{m.lookback_days} days</TableCell>
											<TableCell>{m.processed_folder}</TableCell>
											<TableCell className="itBudgetMuted">{m.last_read ? fmtDateTime(m.last_read) : "—"}</TableCell>
											<TableCell>{m.access ? <span style={pillStyle(m.access === "OK" ? GREEN : RED)}>{m.access}</span> : "—"}</TableCell>
											<TableCell>
												<span style={pillStyle(m.active ? GREEN : GRAY)}>{m.active ? "Active" : "Off"}</span>
											</TableCell>
											{canEdit && (
												<TableCell align="right">
													<IconButton size="small" aria-label="Edit mailbox" onClick={() => startEdit(m)}>
														<EditIcon fontSize="small" />
													</IconButton>
												</TableCell>
											)}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
					<div className="itBudgetMuted" style={{ marginTop: 6 }}>
						Access comes from the latest run's automation log. A mailbox the collector cannot open shows Denied and is named in that run's warning; the other mailboxes still run.
					</div>
				</div>
				{editing !== null && (
					<div className="itBudgetSplitSide">
						<div className="itBudgetCard" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
							<div className="itBudgetOverline">{editing === "new" ? "Add mailbox" : "Edit mailbox"}</div>
							<Collapse in={Boolean(error)}>
								<Alert severity="error">{error}</Alert>
							</Collapse>
							<TextField size="small" label="Email Address" required placeholder="name@j2consultants.com" value={form.email_address} onChange={(e) => setForm({ ...form, email_address: e.target.value })} />
							<TextField size="small" label="Display Name" placeholder="e.g. Accounts Payable (shared)" value={form.display_name || ""} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
							<div className="itBudgetFormGrid">
								<TextField size="small" label="Lookback (days)" value={form.lookback_days} onChange={(e) => setForm({ ...form, lookback_days: e.target.value })} />
								<TextField size="small" label="Processed Folder" value={form.processed_folder} onChange={(e) => setForm({ ...form, processed_folder: e.target.value })} />
							</div>
							<FormControlLabel control={<Checkbox checked={Boolean(form.active)} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label="Active" />
							{editing === "new" && (
								<div className="itBudgetWarning">
									<GroupAddIcon fontSize="small" />
									<span>Also add this mailbox to the IT Budget Invoice Mailboxes group, or the next run will report it as Denied.</span>
								</div>
							)}
							<div className="itBudgetDialogButtons">
								<Button variant="outlined" onClick={() => setEditing(null)} disabled={saving}>
									Cancel
								</Button>
								<Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
									Save Mailbox
								</Button>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default MailboxesTab;
